/**
 * The ranked view of the comparison workbench — the default layout since 2026-11-03.
 *
 * The matrix (items down, one column per supplier) stops working at real scale: 300 items × 20
 * suppliers, where most suppliers quoted only a slice of the list, means the purchaser scrolls
 * sideways past a row of dashes on every item to find the two prices that exist. This view lists,
 * per row, only the suppliers who DID quote, cheapest first.
 *
 * The purchaser does not pick on price alone: one supplier taking most of the enquiry saves a
 * delivery charge per extra supplier, so `supplierCoverage` says how much of the list each one can
 * take. Discounts and offers arrive in the supplier's remark, which the chip shows verbatim.
 *
 * Display only. Ranking, best-price and allocation rules are `lib/compare.ts`'s — the same ones
 * the matrix uses, so the two views can never disagree about which offer is cheapest.
 */

import { bestUnitPrice, isBestOffer, type NormalisedItem, type NormalisedOffer } from './compare'
import type { AllocationRow } from './types'

/** Chips shown on a row before "+N more" — enough for the usual handful, readable at twenty. */
export const MAX_COLLAPSED_OFFERS = 5

export interface RankedOffer {
  offer: NormalisedOffer
  /** 1-based position among the suppliers shown. */
  rank: number
  /** Cheapest on the row among the suppliers shown (ties all count). */
  isBest: boolean
  /** How far above the row's cheapest, in percent; 0 for the cheapest. */
  premiumPct: number
}

/**
 * One entry per VISIBLE supplier who quoted this row, cheapest first.
 *
 * Built from `bySupplier` — one offer per supplier, their cheapest — because a chip is a click
 * target that picks a SUPPLIER, exactly like a matrix column. `bySupplier` is filled from the
 * sorted `offers`, so its insertion order is already cheapest-first.
 */
export function rankOffers(item: NormalisedItem, visibleSuppliers: readonly string[]): RankedOffer[] {
  const best = bestUnitPrice(item, visibleSuppliers)
  const shown = [...item.bySupplier.values()].filter((o) => visibleSuppliers.includes(o.supplier))
  return shown.map((offer, i) => ({
    offer,
    rank: i + 1,
    isBest: isBestOffer(offer, best),
    premiumPct: best && best > 0 && !isBestOffer(offer, best) ? ((offer.unitInBase - best) / best) * 100 : 0,
  }))
}

export interface SupplierCoverage {
  supplier: string
  /** Rows this supplier quoted. */
  quoted: number
  /** Rows where this supplier is cheapest among those shown. */
  cheapest: number
  /** Rows currently allocated (any quantity) to this supplier. */
  picked: number
}

/**
 * How much of the enquiry each visible supplier can take — most items quoted first, then most
 * items cheapest on. Suppliers who quoted nothing in `items` are left out.
 */
export function supplierCoverage(
  items: readonly NormalisedItem[],
  visibleSuppliers: readonly string[],
  allocations: readonly AllocationRow[],
): SupplierCoverage[] {
  const bySupplier = new Map<string, SupplierCoverage>()
  const entry = (supplier: string) => {
    let found = bySupplier.get(supplier)
    if (!found) {
      found = { supplier, quoted: 0, cheapest: 0, picked: 0 }
      bySupplier.set(supplier, found)
    }
    return found
  }

  for (const item of items) {
    for (const ranked of rankOffers(item, visibleSuppliers)) {
      const row = entry(ranked.offer.supplier)
      row.quoted += 1
      if (ranked.isBest) row.cheapest += 1
    }
  }

  // Keyed by row identity — `item_code` alone is not unique (two enquiry lines, one item).
  const rowKeys = new Set(items.map((i) => `${i.item_code}::${i.enquiry_row_no}`))
  const pickedRows = new Map<string, Set<string>>()
  for (const a of allocations) {
    const key = `${a.item_code}::${a.enquiry_row_no}`
    if (a.allocated_qty <= 0 || !rowKeys.has(key) || !bySupplier.has(a.supplier)) continue
    const rows = pickedRows.get(a.supplier) ?? new Set<string>()
    rows.add(key)
    pickedRows.set(a.supplier, rows)
  }
  for (const [supplier, rows] of pickedRows) entry(supplier).picked = rows.size

  return [...bySupplier.values()].sort((a, b) => b.quoted - a.quoted || b.cheapest - a.cheapest)
}
